import vendorDashboardAPI from '@/utils/api/vendorDashboard';
import { genericError, genericSuccess } from '@/utils/alerts.service';
import router from '@/router/dashboard.js';
import { fileUpload } from '@/store/vendorDashboard/modules/fileUpload';
import { charCountForHtmlContent } from '@/utils/Inputs/htmlManipulationFunctions';

import Vue from 'vue';

function getNewIntegrationPartnerForm() {
  return {
    name: '',
    aboutUs: '',
    image: '',
  };
}

export const addIntegrationPartner = {
  namespaced: true,
  state() {
    return {
      candidates: [],
      query: '',
      newIntegrationPartnerForm: getNewIntegrationPartnerForm(),
      errors: {},
      currentPage: 1,
      count: 0,
      pageSize: 47,
      loading: false,
    };
  },
  getters: {
    hasChanges(state) {
      return (
        JSON.stringify(getNewIntegrationPartnerForm()) !==
        JSON.stringify(state.newIntegrationPartnerForm)
      );
    },
    candidates(state) {
      return state.candidates.filter(item => {
        try {
          return item.name.toLowerCase().includes(state.query.toLowerCase());
        } catch {
          return item.name.includes(state.query);
        }
      });
    },
    pagination(state) {
      return {
        previous: state.previous,
        current: state.current,
        next: state.next,
      };
    },
    query(state) {
      return state.query;
    },
  },
  mutations: {
    setError(state, data) {
      Vue.set(state.errors, data.key, {
        label: data.label,
        state: data.state,
        message: data.message,
      });
    },

    clearNewIntegrationPartnerForm(state) {
      state.newIntegrationPartnerForm = getNewIntegrationPartnerForm();
    },
    setCandidates(state, data) {
      state.candidates = data;
      state.candidates.sort((a, b) => {
        const first = a.name.toLowerCase();
        const second = b.name.toLowerCase();
        return first.localeCompare(second, undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      });
    },

    setQuery(state, query) {
      state.query = query;
    },

    setImage(state, data) {
      state.newIntegrationPartnerForm.image = data.uri || '';
    },

    partnerAdded(state, res) {
      state.candidates = state.candidates.filter(candidate => {
        return candidate.id !== res.data.vendor;
      });
    },

    updateNewIntegrationPartnerForm(state, payload) {
      Vue.set(state.newIntegrationPartnerForm, payload.key, payload.value);
    },

    setCurrentPage(state, page) {
      state.currentPage = page;
    },

    setCount(state, count) {
      state.count = count;
    },

    setLoading(state, bool) {
      state.loading = bool;
    },
  },

  actions: {
    ...fileUpload.actions,

    async fetchCandidates({ state, commit, dispatch }, page = 1) {
      let url = `/dashboard_vendor/integration_partners/candidate/?page=${page}&page_size=${state.pageSize}&search=${state.query}`;
      commit('setLoading', true);
      return vendorDashboardAPI
        .getIntegrationCandidates(url)
        .then(data => {
          commit('setCount', data.count);
          commit('setCandidates', data.results);
          return true;
        })
        .catch(() => {
          dispatch('showUserMessage', { ...genericError }, { root: true });
        })
        .finally(() => {
          commit('setLoading', false);
        });
    },

    searchCandidates({ commit, dispatch }, query) {
      commit('setQuery', query);
      commit('setCurrentPage', 1);
      dispatch('fetchCandidates');
    },

    async addPartner({ commit, dispatch }, vendor) {
      let data = { vendor: vendor };
      return vendorDashboardAPI
        .createIntegrationPartnership(data)
        .then(data => {
          dispatch('showUserMessage', { ...genericSuccess }, { root: true });
          commit('partnerAdded', data);
        })
        .catch(() => {
          dispatch('showUserMessage', { ...genericError }, { root: true });
        });
    },

    async checkMainFields({ state, commit }) {
      commit('setError', {
        key: 'name',
        state: !state.newIntegrationPartnerForm.name,
        label: 'Company name',
      });
      commit('setError', {
        key: 'image',
        state: !state.newIntegrationPartnerForm.image,
        label: 'Company logo',
      });
      commit('setError', {
        key: 'aboutUs',
        state:
          charCountForHtmlContent(state.newIntegrationPartnerForm.aboutUs) ===
          0,
        label: 'About',
      });
    },

    async createPartner({ dispatch, state, commit }) {
      await dispatch('checkMainFields');
      if (Object.keys(state.errors).length) {
        for (let key of Object.keys(state.errors)) {
          if (state.errors[key].state) {
            dispatch(
              'showUserMessage',
              {
                success: false,
                title: 'Oops!',
                message:
                  'Please fill in the ' + state.errors[key].label + ' field.',
              },
              { root: true }
            );
            return;
          }
        }
      }

      commit('resetSaveProgress', null, { root: true });
      commit('setSaveTotalItems', 1, { root: true });
      commit('setSaving', 'saving', { root: true });

      await vendorDashboardAPI
        .createManualIntegrationPartnership(state.newIntegrationPartnerForm)
        .then(() => {
          dispatch('showUserMessage', { ...genericSuccess }, { root: true });
          commit('clearNewIntegrationPartnerForm');
          router.push({ name: 'IntegrationPartners' });
        })
        .catch(() => {
          dispatch('showUserMessage', { ...genericError }, { root: true });
        });
      commit('setSaving', 'idle', { root: true });
    },

    setCurrentPage({ commit }, page) {
      commit('setCurrentPage', page);
    },
  },
};
